import { supabase } from "../supabaseClient";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export function humanizeEdgeFunctionError(message: string): string {
  if (message === "Failed to send a request to the Edge Function") {
    return "Impossible de joindre le serveur. Vérifiez votre connexion internet, désactivez un éventuel bloqueur de pub, ou réessayez dans quelques instants.";
  }
  if (message === "Edge Function returned a non-2xx status code") {
    return "Le serveur a refusé la requête. Déconnectez-vous, reconnectez-vous, puis réessayez.";
  }
  return message;
}

async function invokeViaFetch<T>(
  name: string,
  body: Record<string, unknown>
): Promise<{ data: T | null; error: string | null }> {
  if (!supabaseUrl || !supabaseAnonKey) {
    return { data: null, error: "Configuration Supabase manquante." };
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token ?? supabaseAnonKey;

  let response: Response;
  try {
    response = await fetch(`${supabaseUrl}/functions/v1/${name}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Erreur réseau";
    return { data: null, error: humanizeEdgeFunctionError(msg) };
  }

  let payload: T & { error?: string };
  try {
    payload = (await response.json()) as T & { error?: string };
  } catch {
    return {
      data: null,
      error: response.ok
        ? "Réponse serveur invalide."
        : humanizeEdgeFunctionError(`Erreur serveur (${response.status})`),
    };
  }

  if (!response.ok) {
    return {
      data: null,
      error: humanizeEdgeFunctionError(payload.error ?? `Erreur serveur (${response.status})`),
    };
  }

  if (payload.error) {
    return { data: null, error: payload.error };
  }

  return { data: payload as T, error: null };
}

/** Appelle une Edge Function Supabase avec repli fetch si invoke() échoue côté navigateur. */
export async function invokeEdgeFunction<T = Record<string, unknown>>(
  name: string,
  body: Record<string, unknown>
): Promise<{ data: T | null; error: string | null }> {
  try {
    const { data, error } = await supabase.functions.invoke(name, { body });
    if (!error) {
      const payload = (data ?? {}) as T & { error?: string };
      if (payload.error) return { data: null, error: payload.error };
      return { data: payload as T, error: null };
    }

    const raw = error.message ?? "Erreur inconnue";
    if (raw === "Failed to send a request to the Edge Function") {
      return invokeViaFetch<T>(name, body);
    }

    return { data: null, error: humanizeEdgeFunctionError(raw) };
  } catch (err) {
    const fallback = await invokeViaFetch<T>(name, body);
    if (!fallback.error) return fallback;
    const msg = err instanceof Error ? err.message : "Erreur inconnue";
    return { data: null, error: humanizeEdgeFunctionError(msg) };
  }
}
